import { useState, useRef, useEffect } from "react";
import { useAutocomplete } from "./hooks/useAutocomplete";
import { sanitizeQuery, validateSearchQuery } from "./lib/guardrails";
import { Alert, AlertIcon, AlertTitle } from "./components/ui/alert";
import { RiCheckboxCircleFill } from "@remixicon/react";
import Navbar from "./components/navbar";
import CountryDropdown from "./components/CountryDropdown";
import DomainDropdown from "./components/DomainDropdown";
import SubdomainDropdown from "./components/SubdomainDropdown";
import axios from "axios";
import { BASE_URL } from "./apiConfig";
import RippleWaveLoader from "./components/ui/RippleWaveLoader";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import Footer from "./components/Footer";
import { v4 as uuidv4 } from "uuid";

// Add global keyframes once (doesn't affect UI layout)
const globalStyles = `
@keyframes ellipsisAnim {
  0% { opacity:0.2; transform: translateY(0); }
  20% { opacity:1; transform: translateY(-4px); }
  40% { opacity:0.4; transform: translateY(0); }
  100% { opacity:0.2; transform: translateY(0); }
}
`;

// Add this type definition near the top with other types
type Sector = {
  name: string;
  value: string;
  isCustom?: boolean;
};

const DiscoverPage = () => {
  // const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchBarOpen, setSearchBarOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState({
    name: "India",
    value: "india",
    code: "IN",
  });
  const [selectedDomain, setSelectedDomain] = useState<Sector>({
    name: "Select Sector",
    value: "",
    isCustom: false
  });
  const [selectedSubdomain, setSelectedSubdomain] = useState({
    name: "Select Subsector",
    value: "",
  });
  const [responseData, setResponseData] = useState<any>(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  // Use stable string IDs for openSources (avoid index-based)
  const [openSources, setOpenSources] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "database" | "live">("all");
  const [loading, setLoading] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;
  const searchBarRef = useRef<HTMLDivElement>(null);
  const sourceButtonRef = useRef<HTMLButtonElement | null>(null);
  const sourceDropdownRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Text search input
  const [textQuery, setTextQuery] = useState("");
  const acDiscover = useAutocomplete(textQuery);
  const submitRef = useRef<HTMLButtonElement | null>(null);

  // Add this state to hold the searched text
  const [searchedText, setSearchedText] = useState<string | null>(null);

  // New request tracking and pagination state
  const [requestId, setRequestId] = useState<string | null>(null);
  const [previousRequestId, setPreviousRequestId] = useState<string | null>(null);
  const [searchQueries, setSearchQueries] = useState<Record<string, boolean>>({});
  const [lastPayload, setLastPayload] = useState<any>(null);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [liveLoading, setLiveLoading] = useState(false); // <--- add this
  // Prevent auto-submit during restoration
  const restoringRef = useRef(true);
  const mountedRef = useRef(true);
  // Tracks the ID of the most recently started navigation search — stale searches are discarded
  const activeNavSearchRef = useRef<string | null>(null);
  // Tracks the AbortController for the current in-flight navigation search
  const navSearchAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  // Helper to merge new items into session results (safe if unmounted)
  const persistResultsToSession = (newItems: any[]) => {
    try {
      const saved = sessionStorage.getItem('discoveryResults');
      const savedObj = saved ? JSON.parse(saved) : {};
      const prevData = Array.isArray(savedObj?.data) ? savedObj.data : [];
      const combined = [...prevData, ...newItems];
      sessionStorage.setItem('discoveryResults', JSON.stringify({ ...savedObj, data: combined }));
    } catch {}
  };
  const persistSearchQueriesToSession = (sq: any) => {
    try { sessionStorage.setItem('discoverySearchQueries', JSON.stringify(sq)); } catch {}
  };
  // Close sources dropdown on outside click or ESC
  useEffect(() => {
    if (openSources === null) return;

    const handleOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (
        (sourceDropdownRef.current && target && sourceDropdownRef.current.contains(target)) ||
        (sourceButtonRef.current && target && sourceButtonRef.current.contains(target))
      ) {
        return;
      }
      setOpenSources(null);
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenSources(null);
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [openSources]);

  // Close search bar on outside click
  useEffect(() => {
    if (!searchBarOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchBarRef.current &&
        !searchBarRef.current.contains(event.target as Node)
      ) {
        setSearchBarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchBarOpen]);

  useEffect(() => {
    setSelectedSubdomain({ name: "Select Subsector", value: "" });
  }, [selectedDomain]);

  // Scroll to top functionality
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 200);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // const toggleMobileMenu = () => {
  //   setMobileMenuOpen(!mobileMenuOpen);
  // };

  const handleSearchIconClick = () => {
    setSearchBarOpen(!searchBarOpen);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  type DomainKey =
    | "Manufacturing"
    | "Retail"
    | "Healthcare"
    | "AgriTech"
    | "Finance"
    | "Education"
    | "Logistics";

  const subdomainOptionsMap: Record<DomainKey, string[]> = {
    Manufacturing: [
      "Automotive",
      "Electronics",
      "Food & Beverage",
      "Textiles",
      "Chemicals",
      "Pharmaceuticals",
      "Machinery",
    ],
    Retail: [
      "E-commerce",
      "Grocery",
      "Fashion & Apparel",
      "Electronics Retail",
      "Home & Furniture",
      "Beauty & Personal Care",
    ],
    Healthcare: [
      "Hospitals",
      "Pharmaceuticals",
      "Medical Devices",
      "Telemedicine",
      "Health Insurance",
      "Diagnostics",
    ],
    AgriTech: [
      "Precision Farming",
      "Supply Chain & Logistics",
      "Farm Management Software",
      "Agri-finance",
      "Crop Monitoring",
      "Livestock Management",
    ],
    Finance: [
      "Banking",
      "Insurance",
      "Investment & Asset Management",
      "Payments & Fintech",
      "Lending",
      "Wealth Management",
    ],
    Education: [
      "K-12",
      "Higher Education",
      "EdTech Platforms",
      "Corporate Training",
      "Test Preparation",
      "Special Education",
    ],
    Logistics: [
      "Freight & Shipping",
      "Warehousing",
      "Last Mile Delivery",
      "Supply Chain Management",
      "Inventory Management",
      "Fleet Management",
    ],
  };

  const getSubdomainOptions = (domain: Sector) => {
    const base = [
      { name: "Select Subsector", value: "" },
      { name: "Enter Custom Subsector", value: "custom", isCustom: true }
    ];

    if (!domain.value || domain.isCustom) {
      return base;
    }

    if (subdomainOptionsMap[domain.value as DomainKey]) {
      const mapped = subdomainOptionsMap[domain.value as DomainKey].map(name => ({
        name,
        value: name.toLowerCase().replace(/ & | /g, "_")
      }));
      return [
        base[0],
        ...mapped,
        base[1]
      ];
    }

    return base;
  };

  const subdomainOptions = getSubdomainOptions(selectedDomain);

  const capitalizeFirstLetter = (string: string) => {
    if (!string) return string;
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Helper to generate new request ID
  const generateRequestId = () => uuidv4();

  // Centralized helper to start a new request and return ids
  const beginNewRequest = () => {
    const prevId = requestId;
    const newId = generateRequestId();
    if (prevId) setPreviousRequestId(prevId);
    setRequestId(newId);
    return { newId, prevId };
  };

  // Fetch further live batches for pagination, using server-provided search_queries
  const fetchNextSearchBatch = async (force: boolean = false) => {
    if (fetchingMore) return;
    if (!lastPayload || !requestId) {
      console.warn("Pagination aborted: missing lastPayload or requestId.", { lastPayload, requestId, previousRequestId });
      return;
    }

    const hasMoreQueries = Object.values(searchQueries).some(v => v === false);
    if (!hasMoreQueries && !force) return;

    try {
      setFetchingMore(true);
      const payload: any = {
        ...lastPayload,
        is_db: false,
        request_id: requestId,
        previous_request_id: previousRequestId || null,
        search_queries: searchQueries && Object.keys(searchQueries).length ? searchQueries : undefined
      };

      const res = await axios.post(`${BASE_URL}/search_problems`, payload);
      const incoming = res.data?.data || [];
      const incomingNormalized = normalizeItems(incoming);

      // persist to session so it survives navigation
      persistResultsToSession(incomingNormalized);
      if (res.data?.search_queries && typeof res.data.search_queries === 'object') {
        persistSearchQueriesToSession(res.data.search_queries);
      }

      if (mountedRef.current) {
        setResponseData((prev: any) => {
          const existing = Array.isArray(prev?.data) ? prev.data : [];
          return { ...prev, data: removeDuplicatesByTitle([...existing, ...incomingNormalized]) };
        });
        if (res.data?.search_queries && typeof res.data.search_queries === 'object') {
          setSearchQueries(res.data.search_queries);
        }
      }
    } catch (err) {
      console.error("Error fetching pagination batch:", err);
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 3000);
    } finally {
      setFetchingMore(false);
      setLoading(false);
    }
  };

  const getFilteredItems = () => {
    return (responseData?.data || []).filter((item: any) =>
      filter === "all" ? true : filter === "database" ? !!item.usecase_id : !item.usecase_id
    );
  };

  // Smart page change: fetch more if needed
  const handlePageChange = (newPage: number, opts?: { force?: boolean }) => {
    const desiredPage = Math.max(1, newPage);
    const force = opts?.force ?? false;
    const filteredData = getFilteredItems();
    const neededItems = desiredPage * itemsPerPage;

    setCurrentPage(desiredPage);
    window.scrollTo({ top: 300, behavior: "smooth" });

    if (filteredData.length < neededItems && !fetchingMore) {
      const hasMoreQueries = Object.values(searchQueries).some(v => v === false);
      if (hasMoreQueries || force) {
        fetchNextSearchBatch(!!force);
      } else {
        console.warn("No more queries available and no force flag; not fetching next page.", {
          desiredPage,
          filteredDataLength: filteredData.length,
          neededItems
        });
      }
    }
  };

  // Submit flow: DB then Live; track request ids and pagination state
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isTextSearch = textQuery.trim() !== "";

    if (isTextSearch) {
      setSearchedText(textQuery.trim());
    } else {
      setSearchedText(null);
    }

    setLoading(true);
    setLiveLoading(true);
    setCurrentPage(1);
    setResponseData(null);

    const { newId: newRequestId, prevId: prevId } = beginNewRequest();

    try {
      const basePayload: any = isTextSearch ? {
        search_query: textQuery.trim(),
      } : {
        sector: capitalizeFirstLetter(selectedDomain.value),
        subdomain: capitalizeFirstLetter(selectedSubdomain.value),
        country: selectedCountry.value === 'worldwide' ? '' : selectedCountry.code,
      };

      setLastPayload({ ...basePayload });

      // Get user id from localStorage
      const requested_user_id = localStorage.getItem("uid");

      // First: DB results
      const dbPayload = {
        ...basePayload,
        is_db: true,
        request_id: newRequestId,
        previous_request_id: prevId || null,
        ...(requested_user_id ? { requested_user_id } : {}),
      };

      const dbRes = await axios.post(`${BASE_URL}/search_problems`, dbPayload);
      const dbRaw = dbRes.data?.data || [];
      const dbData = normalizeItems(dbRaw);

      setResponseData({ ...dbRes.data, data: dbData });

      if (Array.isArray(dbData) && dbData.length > 0) {
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
        setLoading(false);
      }

      if (dbRes.data?.search_queries && typeof dbRes.data.search_queries === 'object') {
        setSearchQueries(dbRes.data.search_queries);
      }

      // Then: Live results
      const livePayload = {
        ...basePayload,
        is_db: false,
        request_id: newRequestId,
        previous_request_id: prevId || null,
      };

      const aiRes = await axios.post(`${BASE_URL}/search_problems`, livePayload);
      const aiRaw = aiRes.data?.data || [];
      const aiData = normalizeItems(aiRaw);

      // persist live results immediately so they’re available when coming back
      persistResultsToSession(aiData);
      if (aiRes.data?.search_queries && typeof aiRes.data.search_queries === 'object') {
        persistSearchQueriesToSession(aiRes.data.search_queries);
      }

      if (mountedRef.current) {
        setResponseData((prev: any) => {
          const existing = Array.isArray(prev?.data) ? prev.data : [];
          const combined = [...existing, ...aiData];
          // Remove duplicates from the combined array
          const deduplicated = removeDuplicatesByTitle(combined);
          return { ...prev, data: deduplicated };
        });
        if (aiRes.data?.search_queries && typeof aiRes.data.search_queries === 'object') {
          setSearchQueries(aiRes.data.search_queries);
        }
        setLiveLoading(false);
        if (!dbData || dbData.length === 0) {
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('API call error:', error);
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 3000);
      setResponseData((prev: any) => (prev?.data?.length ? prev : null));
      setLoading(false);
      setLiveLoading(false); // <--- reset on error
    } finally {
      // keep selections
    }
  };

  // Navigation-based search_query flow with request ids and persistence
  useEffect(() => {
    if (location.state?.search_query) {
      const safeQuery = sanitizeQuery(location.state.search_query);

      const queryCheck = validateSearchQuery(safeQuery, true);
      if (!queryCheck.valid) {
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
        return;
      }

      // Pre-fill search bar and save searched text
      setTextQuery(safeQuery);
      setSearchedText(safeQuery);

      // Reset filters for text search
      setSelectedCountry({ name: "Select Country", value: "", code: "" });
      setSelectedDomain({ name: "Select Sector", value: "" });
      setSelectedSubdomain({ name: "Select Subsector", value: "" });
      setFilter("all");
      setCurrentPage(1);

      // Abort any previous in-flight navigation search request
      navSearchAbortRef.current?.abort();
      const controller = new AbortController();
      navSearchAbortRef.current = controller;

      // Stamp this search so stale concurrent searches can be detected and discarded
      const searchToken = `${safeQuery}-${Date.now()}`;
      activeNavSearchRef.current = searchToken;

      (async () => {
        setLoading(true);
        setResponseData(null);

        const { newId: newRequestId, prevId } = beginNewRequest();

        // Save base payload to reuse for pagination (search query based)
        const basePayload = { search_query: safeQuery };
        setLastPayload({ ...basePayload });
        setLiveLoading(true);
        const requested_user_id = localStorage.getItem("uid");
        try {
          // DB call
          const dbPayload = {
            search_query: safeQuery,
            is_db: true,
            request_id: newRequestId,
            previous_request_id: prevId || null,
            ...(requested_user_id ? { requested_user_id } : {})
          };

          const dbRes = await axios.post(`${BASE_URL}/search_problems`, dbPayload, { signal: controller.signal });

          // Discard if a newer search has started
          if (activeNavSearchRef.current !== searchToken) return;

          const dbRaw = dbRes.data?.data || [];
          const dbData = normalizeItems(dbRaw);
          setResponseData({ ...dbRes.data, data: dbData });

          if (Array.isArray(dbData) && dbData.length > 0) {
            setShowSuccessAlert(true);
            setTimeout(() => setShowSuccessAlert(false), 3000);
            setLoading(false);
          }

          if (dbRes.data?.search_queries && typeof dbRes.data.search_queries === 'object') {
            setSearchQueries(dbRes.data.search_queries);
          }

          // Live call
          const livePayload = {
            search_query: safeQuery,
            is_db: false,
            request_id: newRequestId,
            previous_request_id: prevId || null
          };

          const liveRes = await axios.post(`${BASE_URL}/search_problems`, livePayload, { signal: controller.signal });

          // Discard if a newer search has started
          if (activeNavSearchRef.current !== searchToken) return;

          const liveRaw = liveRes.data?.data || [];
          const liveData = normalizeItems(liveRaw);

          // persist live results immediately
          persistResultsToSession(liveData);
          if (liveRes.data?.search_queries && typeof liveRes.data.search_queries === 'object') {
            persistSearchQueriesToSession(liveRes.data.search_queries);
          }

          if (mountedRef.current) {
            setResponseData((prev: any) => {
              const existing = Array.isArray(prev?.data) ? prev.data : [];
              const combined = [...existing, ...liveData];
              const deduplicated = removeDuplicatesByTitle(combined);
              return { ...prev, data: deduplicated };
            });
            if (!Array.isArray(dbData) || dbData.length === 0) {
              setLoading(false);
              if (liveData.length > 0) {
                setShowSuccessAlert(true);
                setTimeout(() => setShowSuccessAlert(false), 3000);
              }
            }
            if (liveRes.data?.search_queries && typeof liveRes.data.search_queries === 'object') {
              setSearchQueries(liveRes.data.search_queries);
            }
            setLiveLoading(false);
          }
        } catch (error) {
          if (activeNavSearchRef.current !== searchToken) return;
          // Silently drop aborted requests (user navigated away)
          if (axios.isCancel(error)) return;
          console.error('Search flow error:', error);
          setShowErrorAlert(true);
          setTimeout(() => setShowErrorAlert(false), 3000);
          setLoading(false);
        } finally {
          if (activeNavSearchRef.current === searchToken) {
            setSearchBarOpen(false);
            setLiveLoading(false);
          }
        }
      })();

      // Clear location state
      window.history.replaceState({}, document.title);
    }
    // Abort in-flight request when component unmounts or a new location.state arrives
    return () => { navSearchAbortRef.current?.abort(); };
  }, [location.state]);

  // Restore searched text from session on refresh (not when arriving via tag navigation)
  useEffect(() => {
    if (location.state?.search_query) return;
    const savedLastPayload = sessionStorage.getItem('discoveryLastPayload');
    if (savedLastPayload) {
      const lp = JSON.parse(savedLastPayload);
      if (lp?.search_query) setSearchedText(lp.search_query);
    }
  }, []);

  // Save to sessionStorage
  useEffect(() => {
    const hasSelections =
      selectedDomain.value !== "" ||
      selectedSubdomain.name !== "Select Subsector" ||
      selectedCountry.name !== "Select Country" ||
      filter !== "all";

    if (hasSelections) {
      sessionStorage.setItem('discoverySelections', JSON.stringify({
        domain: selectedDomain,
        subdomain: selectedSubdomain,
        country: selectedCountry,
        filter: filter
      }));
    }

    if (responseData?.data?.length > 0) {
      sessionStorage.setItem('discoveryResults', JSON.stringify(responseData));
    }

    if (Object.keys(searchQueries).length > 0) {
      sessionStorage.setItem('discoverySearchQueries', JSON.stringify(searchQueries));
    }

    if (lastPayload) sessionStorage.setItem('discoveryLastPayload', JSON.stringify(lastPayload));
    if (requestId) sessionStorage.setItem('discoveryRequestId', requestId);
    if (previousRequestId) sessionStorage.setItem('discoveryPreviousRequestId', previousRequestId);
  }, [selectedDomain, selectedSubdomain, selectedCountry, filter, responseData, searchQueries, lastPayload, requestId, previousRequestId]);

  // Restore from sessionStorage on mount
  useEffect(() => {
    if (location.state?.selectedDomain) {
      setSelectedDomain(location.state.selectedDomain);
      window.history.replaceState({}, document.title);
    }

    const savedSelections = sessionStorage.getItem('discoverySelections');
    const savedResults = sessionStorage.getItem('discoveryResults');

    if (savedSelections && !location.state?.selectedDomain && !location.state?.search_query) {
      const { domain, subdomain, country, filter: savedFilter } = JSON.parse(savedSelections);
      if (domain) setSelectedDomain(domain);
      if (country) setSelectedCountry(country);
      if (savedFilter) setFilter(savedFilter);

      if (subdomain && domain) {
        setTimeout(() => {
          setSelectedSubdomain(subdomain);
        }, 50);
      }
    }

    if (savedResults && !location.state?.search_query) {
      setResponseData(JSON.parse(savedResults));
    }

    // Only restore search state when NOT arriving via a tag/search navigation
    // (navigation effect will set these correctly for tag clicks)
    if (!location.state?.search_query) {
      const savedSearchQueries = sessionStorage.getItem('discoverySearchQueries');
      if (savedSearchQueries) {
        setSearchQueries(JSON.parse(savedSearchQueries));
      }
      const savedLastPayload = sessionStorage.getItem('discoveryLastPayload');
      if (savedLastPayload) setLastPayload(JSON.parse(savedLastPayload));
      const savedRequestId = sessionStorage.getItem('discoveryRequestId');
      if (savedRequestId) setRequestId(savedRequestId);
      const savedPrevReq = sessionStorage.getItem('discoveryPreviousRequestId');
      if (savedPrevReq) setPreviousRequestId(savedPrevReq);
    }

    setTimeout(() => {
      restoringRef.current = false;
    }, 120);
  }, []);

  useEffect(() => {
    return () => {
      // keep session for UX continuity
    };
  }, []);

  // Pagination helper (UI remains the same)
  const getPaginatedData = (data: any[]) => {
    const filteredData = data.filter((item: any) =>
      filter === "all"
        ? true
        : filter === "database"
        ? item.usecase_id
        : !item.usecase_id
    );

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    return {
      totalItems: filteredData.length,
      items: filteredData.slice(startIndex, endIndex),
      totalPages: Math.ceil(filteredData.length / itemsPerPage)
    };
  };

  // Helpers to detect valid selections for auto-submit
  const isCountrySet = (country: typeof selectedCountry) =>
    country.value !== "" && country.name !== "Select Country";
  const isDomainSet = (domain: Sector) =>
    domain.value !== "" && domain.name !== "Select Sector";
  const isSubdomainSet = (subdomain: typeof selectedSubdomain) =>
    subdomain.value !== "" && subdomain.name !== "Select Subsector";

  const prevFiltersRef = useRef({
    country: selectedCountry,
    domain: selectedDomain,
    subdomain: selectedSubdomain,
  });

  // Modify the auto-submit effect
  useEffect(() => {
    if (restoringRef.current) return;

    const prev = prevFiltersRef.current;
    const countryChanged = prev.country.value !== selectedCountry.value;
    const domainChanged = prev.domain.value !== selectedDomain.value;
    const subdomainChanged = prev.subdomain.value !== selectedSubdomain.value;

    // Update previous values immediately
    prevFiltersRef.current = {
      country: selectedCountry,
      domain: selectedDomain,
      subdomain: selectedSubdomain,
    };

    // If domain actually changed AND subdomain got reset, skip auto-submit
    if (domainChanged && selectedSubdomain.value === "") {
      return;
    }

    const setCount =
      (isCountrySet(selectedCountry) ? 1 : 0) +
      (isDomainSet(selectedDomain) ? 1 : 0) +
      (isSubdomainSet(selectedSubdomain) ? 1 : 0);

    const haveRestoredResults = !!responseData?.data?.length;
    if (haveRestoredResults && (countryChanged || subdomainChanged) === false) {
      return;
    }

    if (
      setCount === 3 &&
      (
        (countryChanged && isCountrySet(selectedCountry)) ||
        (subdomainChanged && isSubdomainSet(selectedSubdomain))
      )
    ) {
      if (!loading) {
        handleSubmit(new Event("submit") as any);
      }
    }
  }, [selectedCountry, selectedDomain, selectedSubdomain, loading, responseData]);

  // Also ensure this useEffect runs after the subdomain reset
  useEffect(() => {
    if (selectedDomain.value !== prevFiltersRef.current.domain.value) {
      setSelectedSubdomain({ name: "Select Subsector", value: "" });
    }
  }, [selectedDomain]);

  return (
    <div
      className="min-h-screen relative flex flex-col overflow-x-hidden discover-page"
      style={{
        background:
          "radial-gradient(35% 19% at 50% 0%, rgba(255, 84, 31, 0.5) 0%, rgba(0, 0, 0, 0) 100%), black",
        color: "white",
      }}
    >
      <style>{globalStyles}</style>
      {/* Mobile-only tweaks */}
      <style>{`
        @media (max-width: 640px) {
          .discover-page .bg-dots { display: none !important; }
          .discover-page .search-title { font-size: 18px !important; }
          .discover-page .filters-card { border-radius: 14px !important; }
          .discover-page .filters-form { padding: 16px 12px !important; gap: 12px 10px !important; }
          .discover-page .h-14 { height: 48px !important; }
          .discover-page .disabled-input { font-size: 12px !important; }
          .discover-page .filters-toolbar { 
            flex-direction: column !important; 
            align-items: center !important; 
            gap: 12px !important; 
            flex-wrap: nowrap !important;
          }
          .discover-page .filter-chips {
            display: flex !important;
            gap: 8px !important;
            justify-content: center !important;
            flex-wrap: nowrap !important;
          }
          .discover-page .filter-chip { padding: 8px 12px !important; font-size: 14px !important; line-height: 1 !important; }
          .discover-page .usecases-grid { gap: 16px !important; }
          .discover-page .usecase-card { padding: 12px 16px 56px 16px !important; }
          .discover-page .usecase-title { font-size: 16px !important; }
          .discover-page .usecase-desc { font-size: 14px !important; line-height: 1.4 !important; }
          .discover-page .source-btn { padding: 8px 10px !important; font-size: 12px !important; }
          .discover-page .pagination { overflow-x: auto; -webkit-overflow-scrolling: touch; padding-bottom: 4px; gap: 6px !important; }
          .discover-page .pagination > button { flex: 0 0 auto; }
          .discover-page .results-range { font-size: 12px !important; }
          .discover-page .loader-wrap { margin-top: 24px !important; }
        }
      `}</style>
      {/* Add Dots Pattern Background */}
      <div
        className="absolute bg-dots"
        style={{
          width: "1106px",
          height: "500px",
          top: "-12px",
          left: "194px",
          backgroundImage: "url('/dots.png')",
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          opacity: 1,
          pointerEvents: "none",
          userSelect: "none",
          zIndex: 0,
        }}
      />
      {/* Side ornaments (desktop only). Update the image paths to your PNG names */}
      <div
        className="hidden md:block absolute pointer-events-none select-none"
        aria-hidden="true"
        style={{
          top: "40%",
          left: "-24px",
          transform: "translateY(-50%)",
          width: "100px",
          height: "250px",
          backgroundImage: "url('./disover_rounded_rectangle.png')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "contain",
          backgroundPosition: "center",
          opacity: 1,
          zIndex: 0,
        }}
      />
      <div
        className="hidden md:block absolute pointer-events-none select-none"
        aria-hidden="true"
        style={{
          top: "40%",
          right: "-24px",
          transform: "translateY(-50%)",
          width: "100px",
          height: "250px",
          backgroundImage: "url('./discover_Circle.png')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "contain",
          backgroundPosition: "center",
          opacity: 1,
          zIndex: 0,
        }}
      />

      {/* Top Nav */}
      <div className="w-full bg-transparent border-b border-white/10 shadow-sm relative z-[1000]">
        <Navbar
          onSearchClick={handleSearchIconClick}
          // mobileMenuOpen={mobileMenuOpen}
          // toggleMobileMenu={toggleMobileMenu}
        />
      </div>

      {/* Add a container div with consistent padding */}
      <div className="w-full max-w-[1440px] mx-auto px-4 md:px-12 lg:px-24 flex flex-col flex-1">
        {/* Page title */}
        <div className="relative z-10 w-full flex flex-col items-center mt-30">
          <button
            onClick={() => navigate("/")}
            className="absolute left-0 top-0 flex items-center gap-1.5 text-sm text-gray-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/10 transition-all border border-white/10"
          >
            ← Back to Chat
          </button>
          <h1
            className="search-title"
            style={{
              fontFamily: "Sora",
              fontWeight: 700,
              fontStyle: "bold",
              fontSize: "20px",
              lineHeight: "150%",
              letterSpacing: "0%",
              textAlign: "center",
              color: "#FFFFFF",
            }}
          >
            Discover
          </h1>

          {/* Show searched text when available */}
          {searchedText && (
            <div
              style={{
                marginTop: 8,
                fontFamily: "Sora",
                fontWeight: 500,
                fontSize: "14px",
                color: "#D9D9D9",
              }}
            >
              Showing results for: <span style={{ color: "#FF541F" }}>{searchedText}</span>
            </div>
          )}
        </div>

        {/* Filters Section */}
        <div className="w-full flex justify-center mt-6 relative z-30">
          <div
            className="w-full rounded-2xl border border-[#474747] bg-transparent filters-card"
          >
            <form
              className="pl-10 pr-2 py-10 flex flex-col gap-6 filters-form"
              onSubmit={handleSubmit}
            >
              {/* Text Search */}
              <div className="flex flex-col gap-2 pr-8">
                <label
                  style={{
                    fontFamily: "Sora",
                    fontWeight: 400,
                    fontSize: "16px",
                    lineHeight: "150%",
                    color: "#808080",
                    borderRadius: "6px",
                    padding: "2px 8px",
                    display: "inline-block",
                  }}
                >
                  Search
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={textQuery}
                    onChange={e => setTextQuery(e.target.value)}
                    onKeyDown={e => acDiscover.handleKeyDown(
                      e,
                      s => { setTextQuery(s); acDiscover.clear(); submitRef.current?.click(); },
                      () => submitRef.current?.click()
                    )}
                    onBlur={() => setTimeout(() => acDiscover.clear(), 150)}
                    placeholder="e.g. 'AI use cases in predictive maintenance' or 'computer vision in healthcare'..."
                    className="w-full h-14 rounded-[12px] pl-4 pr-4 bg-transparent border border-[#1F1F1F] text-white placeholder-[#555] focus:outline-none focus:border-[#FF541F] text-sm"
                    style={{ fontFamily: "Sora" }}
                  />
                  {acDiscover.showSuggestions && acDiscover.suggestions.length > 0 && (
                    <ul className="absolute top-full left-0 right-0 mt-1 z-50 rounded-xl overflow-hidden border border-white/10 shadow-2xl" style={{ background: "#1a1a1a" }}>
                      {acDiscover.suggestions.map((s, i) => (
                        <li key={s}
                          onMouseDown={() => { setTextQuery(s); acDiscover.clear(); submitRef.current?.click(); }}
                          className="px-4 py-2.5 text-sm cursor-pointer transition-colors"
                          style={{ background: i === acDiscover.activeSuggestion ? "rgba(255,84,31,0.15)" : "transparent", color: i === acDiscover.activeSuggestion ? "#FF541F" : "#d1d5db" }}>
                          {s}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Dropdowns + Button row */}
              <div className="grid grid-cols-1 md:grid-cols-[auto_0.5rem_auto_0.5rem_auto_auto] items-end gap-x-4">

              {/* Country */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="country"
                  style={{
                    fontFamily: "Sora",
                    fontWeight: 400,
                    fontStyle: "normal",
                    fontSize: "16px",
                    lineHeight: "150%",
                    letterSpacing: "0%",
                    color: "#808080",
                    borderRadius: "6px",
                    padding: "2px 8px",
                    display: "inline-block",
                  }}
                >
                  Country
                </label>
                <div className="h-14">
                  <CountryDropdown
                    selected={selectedCountry}
                    setSelected={setSelectedCountry}
                  />
                </div>
              </div>

              {/* Divider */}
              <div className="hidden md:flex justify-center items-end h-14">
                <div className="w-px h-full border-l" style={{ borderColor: "#1F1F1F", borderWidth: "1px" }} />
              </div>

              {/* Sector */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="domain"
                  style={{
                    fontFamily: "Sora",
                    fontWeight: 400,
                    fontStyle: "normal",
                    fontSize: "16px",
                    lineHeight: "150%",
                    letterSpacing: "0%",
                    color: "#808080",
                    borderRadius: "6px",
                    padding: "2px 8px",
                    display: "inline-block",
                  }}
                >
                  Sector
                </label>
                <div className="h-14">
                  <DomainDropdown
                    selected={selectedDomain}
                    setSelected={setSelectedDomain}
                  />
                </div>
              </div>

              {/* Divider */}
              <div className="hidden md:flex justify-center items-end h-14">
                <div className="w-px h-full border-l" style={{ borderColor: "#1F1F1F", borderWidth: "1px" }} />
              </div>

              {/* Subsector */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="subdomain"
                  style={{
                    fontFamily: "Sora",
                    fontWeight: 400,
                    fontStyle: "normal",
                    fontSize: "16px",
                    lineHeight: "150%",
                    letterSpacing: "0%",
                    color: "#808080",
                    borderRadius: "6px",
                    padding: "2px 8px",
                    display: "inline-block",
                  }}
                >
                  Subsector
                </label>
                <div className="h-14">
                  {selectedDomain.value ? (
                    <SubdomainDropdown
                      selected={selectedSubdomain}
                      options={subdomainOptions}
                      setSelected={setSelectedSubdomain}
                      isCustomDomain={
                        !subdomainOptionsMap[selectedDomain.value as DomainKey]
                      }
                    />
                  ) : (
                    <input
                      type="text"
                      value="Select a sector first"
                      disabled
                      className="w-full md:w-[240px] h-14 cursor-not-allowed text-gray-400 rounded-[12px] pl-5 pr-2 py-2 bg-transparent border border-[#1F1F1F] disabled-input"
                    />
                  )}
                </div>
              </div>

              {/* Search Button */}
              <div className="flex">
                <button
                  ref={submitRef}
                  type="submit"
                  className="w-full h-14 px-6 rounded-[12px] text-white font-semibold border border-[#1F1F1F] transition-all duration-200"
                  style={{
                    background: "#FF541F",
                  }}
                  disabled={loading}
                >
                  Search
                </button>
              </div>
              </div>{/* end dropdowns row */}
            </form>
          </div>
        </div>

        {/* Alerts */}
        {showSuccessAlert && (
          <div className="fixed top-3 left-1/2 z-50 -translate-x-1/2 w-full max-w-md">
            <Alert variant="success" close={true} onClose={() => setShowSuccessAlert(false)}>
              <AlertIcon>
                <RiCheckboxCircleFill />
              </AlertIcon>
              <AlertTitle>Data received successfully!</AlertTitle>
            </Alert>
          </div>
        )}

        {showErrorAlert && (
          <div className="fixed top-3 left-1/2 z-50 -translate-x-1/2 w-full max-w-md">
            <Alert variant="destructive" close={true} onClose={() => setShowErrorAlert(false)}>
              <AlertIcon>
                <span className="text-xl">!</span>
              </AlertIcon>
              <AlertTitle>
                {responseData === null
                  ? "Failed to fetch data. Please try again."
                  : "Please select a domain."}
              </AlertTitle>
            </Alert>
          </div>
        )}

        {/* Content */}
        <main className="relative z-0 flex flex-1 flex-col items-center justify-center">
          {/* Loader */}
          {loading && (
            <div className="w-full flex-1 flex flex-col items-center justify-center mt-36 loader-wrap">
              <div className="flex flex-col items-center">
                <RippleWaveLoader />
                <span className="mt-4 text-gray-200 font-medium text-lg">Loading results...</span>
              </div>
              <div className="min-h-[50vh]"></div>
            </div>
          )}

          {/* Cards Section */}
          {responseData?.data && responseData.data.length > 0 ? (
            <div className="w-full flex flex-col items-center mt-8 px-4">
              {/* Results Header */}
              <div className="w-full max-w-7xl mb-6 flex flex-col gap-3">
                {/* Row 1: label only */}
                <div className="w-full">
                  <span
                    style={{
                      fontFamily: "Sora",
                      fontWeight: 400,
                      fontSize: 16,
                      lineHeight: "150%",
                      color: "#808080",
                    }}
                  >
                    Search Results
                  </span>
                </div>

                {/* Row 2: Filters only (removed Use Cases Found pill) */}
                <div className="w-full relative min-h-[62px]">
                  <div className="flex items-center justify-center gap-4 filters-toolbar">
                    {/* Filter chips */}
                    <div className="flex items-center justify-center gap-3 filter-chips">
                      <button
                        className={`px-4 py-2 rounded-full font-semibold border shadow-sm transition-colors duration-150 filter-chip ${
                          filter === "all"
                            ? "bg-[#FF541F] border-[#FF541F] text-white"
                            : "bg-transparent border-white/10 text-white"
                        }`}
                        onClick={() => setFilter("all")}
                      >
                        All
                      </button>
                      <button
                        className={`px-4 py-2 rounded-full font-semibold border shadow-sm transition-colors duration-150 filter-chip ${
                          filter === "database"
                            ? "bg-[#FF541F] border-[#FF541F] text-white"
                            : "bg-transparent border-white/10 text-white"
                        }`}
                        onClick={() => setFilter("database")}
                      >
                        Database
                      </button>
                      <button
                        className={`px-4 py-2 rounded-full font-semibold border shadow-sm transition-colors duration-150 filter-chip ${
                          filter === "live"
                            ? "bg-[#FF541F] border-[#FF541F] text-white"
                            : "bg-transparent border-white/10 text-white"
                        }`}
                        onClick={() => setFilter("live")}
                      >
                        Live
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cards Grid */}
              {(() => {
                const { totalItems, items } = getPaginatedData(responseData.data);

                return totalItems > 0 ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full usecases-grid">
                      {items.map((item: any, idx: number) => {
                        const uid = item._uid ?? makeUID(item, idx);
                        const isOpen = openSources === uid;

                        return (
                          <div
                            key={uid}
                            className={`relative text-white hover:scale-[1.02] transition-transform duration-200 usecase-card ${isOpen ? "z-50" : ""}`}
                            style={{
                              background: "linear-gradient(0deg, rgba(39, 40, 41, 0.7), rgba(39, 40, 41, 0.7)),linear-gradient(119.3deg, rgba(0, 0, 0, 0) 23.34%, rgba(255, 60, 0, 0.76) 99.36%)",
                              borderRadius: 20,
                              opacity: 1,
                              gap: 12,
                              paddingTop: 12,
                              paddingRight: 24,
                              paddingBottom: 12,
                              paddingLeft: 24,
                              boxShadow: "0deg",
                              border: "1px solid #FFFFFF1A",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                            }}
                          >
                            {/* Top-right label */}
                            <span
                              className="absolute top-2 right-2 z-10"
                              style={{
                                width: 62,
                                height: 18,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                                borderRadius: 20,
                                padding: "1px",
                                background: "#48BB781A",
                                opacity: 1,
                                fontWeight: 600,
                                fontSize: "12px",
                                color: item.usecase_id ? "#3B82F6" : "#22C55E", // blue for db, green for live
                                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                                letterSpacing: "0.01em",
                                border: "none",
                              }}
                            >
                              {item.usecase_id ? "Database" : "Live"}
                            </span>

                            <div style={{ marginTop: "2px" }} /> {/* Professional gap below chip */}

                            <h2
                              className="usecase-title"
                              style={{
                                fontFamily: "IBM Plex Sans, sans-serif",
                                fontWeight: 600,
                                fontStyle: "normal",
                                fontSize: "18px",
                                lineHeight: "120%",
                                letterSpacing: "0%",
                                color: "#FFFFFF",
                                borderRadius: "8px",
                                padding: "2px 8px",
                                marginBottom: "8px",
                                display: "inline-block",
                              }}
                            >
                              {item.title}
                            </h2>
                            <p
                              className="usecase-desc"
                              style={{
                                fontFamily: "Lora, serif",
                                fontWeight: 400,
                                fontStyle: "normal",
                                fontSize: "16px",
                                lineHeight: "19.2px",
                                letterSpacing: "0%",
                                color: "#D9D9D9D9",
                                marginBottom: "8px",
                              }}
                            >
                              {item.description}
                            </p>

                            <div className="mb-2">
                              <div className="relative inline-block">
                                <button
                                  ref={isOpen ? sourceButtonRef : null}
                                  type="button"
                                  className="flex items-center font-semibold text-xs rounded-lg px-3 py-2 transition-colors source-btn"
                                  style={{
                                    gap: 8,
                                    background: "#2A2A2A",
                                    color: "#F3F4F6",
                                    border: "none",
                                  }}
                                  onClick={() => setOpenSources(isOpen ? null : uid)}
                                  aria-expanded={isOpen}
                                  aria-controls={`sources-panel-${uid}`}
                                >
                                  <span>Source</span>
                                  <ChevronDownIcon
                                    className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""} relative top-[1px]`}
                                  />
                                </button>

                                {isOpen && (
                                  <div
                                    ref={sourceDropdownRef}
                                    id={`sources-panel-${uid}`}
                                    role="region"
                                    aria-label="Source links"
                                    className="absolute left-0 mt-2 w-[min(480px,90vw)] z-50"
                                    style={{
                                      background: "rgba(18,18,20,0.95)",
                                      border: "1px solid rgba(67,72,84,0.6)",
                                      borderRadius: 12,
                                      padding: 10,
                                      boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                                      backdropFilter: "blur(12px)",
                                    }}
                                  >
                                    <ul className="flex flex-col gap-2 m-0 p-0">
                                      {item.source.map((src: string, sidx: number) => {
                                        let domain = src;
                                        let displayTitle = src;
                                        try {
                                          const urlObj = new URL(src);
                                          domain = urlObj.hostname.replace(/^www\./i, "");
                                          displayTitle = domain;
                                        } catch (_e) {
                                          // fallback
                                        }

                                        return (
                                          <li key={sidx}>
                                            <a
                                              href={src}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              onClick={(e) => e.stopPropagation()}
                                              className="flex items-start gap-3 p-3 rounded-lg hover:bg-white/5 transition-all duration-200 border border-white/5 hover:border-orange-500/30 group"
                                              style={{ textDecoration: "none" }}
                                            >
                                              <img
                                                src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`}
                                                alt={`${domain} favicon`}
                                                className="flex-shrink-0"
                                                style={{
                                                  width: 32,
                                                  height: 32,
                                                  borderRadius: 6,
                                                  background: "transparent",
                                                  objectFit: "cover",
                                                  filter: "grayscale(100%) opacity(0.95)",
                                                  marginTop: 2,
                                                }}
                                              />
                                              <div style={{ minWidth: 0, flex: 1 }}>
                                                <div
                                                  className="group-hover:text-orange-400 transition-colors"
                                                  style={{
                                                    color: "#ffffff",
                                                    fontWeight: 500,
                                                    fontSize: "0.95rem",
                                                    fontFamily: "Sora, sans-serif",
                                                    marginBottom: 4,
                                                  }}
                                                >
                                                  {displayTitle}
                                                </div>
                                                <div
                                                  style={{
                                                    fontSize: 12,
                                                    color: "#737373",
                                                    wordBreak: "break-all",
                                                    lineHeight: 1.5,
                                                    fontFamily: "Sora, sans-serif",
                                                  }}
                                                >
                                                  {src}
                                                </div>
                                              </div>
                                              <svg
                                                className="w-4 h-4 flex-shrink-0 text-neutral-400 group-hover:text-orange-400 transition-colors"
                                                style={{ marginTop: 4 }}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth={2}
                                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                                />
                                              </svg>
                                            </a>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* View Details Button */}
                            <div style={{ position: "absolute", bottom: 24, right: 24 }}>
                              <button
                                className="flex items-center justify-center"
                                style={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: "50%",
                                  background:
                                    "linear-gradient(119.3deg, #FF541F 23.34%, #FF3C00 96.36%)",
                                  boxShadow: "0 2px 8px rgba(255, 60, 0, 0.15)",
                                  border: "none",
                                  opacity: 1,
                                  cursor: "pointer",
                                  transition: "transform 0.15s",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                                onClick={() => {
                                  const isDb = !!item.usecase_id;
                                  const detailId = isDb ? String(item.usecase_id) : (item._uid ?? uid);
                                  navigate(`/details/${detailId}`, {
                                    state: {
                                      title: item.title,
                                      description: item.description,
                                      domain: item.domain,
                                      subdomain: item.subdomain,
                                      source: Array.isArray(item.source) ? item.source : (item.source ? [item.source] : []),
                                      country: item.country,
                                      ...(isDb && /^\d+$/.test(String(item.usecase_id)) ? { usecase_id: item.usecase_id } : {}),
                                      is_live: !isDb
                                    },
                                  });
                                }}
                                aria-label="View Details"
                              >
                                <svg
                                  width="24"
                                  height="24"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  style={{ display: "block" }}
                                >
                                  <path
                                    d="M7 17L17 7M17 7H9M17 7V15"
                                    stroke="#fff"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      {/* Loader shown when paginating to an empty page while results are loading */}
                      {(fetchingMore || liveLoading) && items.length === 0 && (
                        <div className="col-span-1 md:col-span-3 w-full flex flex-col items-center py-8">
                          <RippleWaveLoader />
                          <span className="mt-4 text-gray-200 font-medium text-lg">Loading more results...</span>
                        </div>
                      )}
                    </div>

                    {/* Pagination */}
                    {(() => {
                      const { totalPages, totalItems } = getPaginatedData(responseData.data);
                      if (totalPages < 1) return null; // safety

                      return (
                        <>
                          <div className="flex justify-center items-center gap-2 mt-8 mb-4 pagination">
                            <button
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                              className={`px-4 py-2 rounded-lg font-medium border ${
                                currentPage === 1
                                  ? "bg-black/40 text-gray-500 border-white/10 cursor-not-allowed"
                                  : "bg-[#181818] text-white border-white/10 hover:bg-[#222]"
                              }`}
                            >
                              Previous
                            </button>

                            {[...Array(totalPages)].map((_, idx) => (
                              <button
                                key={idx}
                                onClick={() => handlePageChange(idx + 1)}
                                className={`w-10 h-10 rounded-lg font-medium border ${
                                  currentPage === idx + 1
                                    ? "bg-[#FF541F] text-white border-transparent"
                                    : "bg-[#181818] text-white border-white/10 hover:bg-[#222]"
                                }`}
                              >
                                {idx + 1}
                              </button>
                            ))}

                            {(() => {
                              const hasMoreQueries = Object.values(searchQueries).some(v => v === false);
                              const isAtEnd = currentPage >= totalPages && !hasMoreQueries;
                              const isDisabled = fetchingMore || liveLoading || isAtEnd;
                              return (
                                <button
                                  onClick={() => {
                                    const needForce = currentPage >= totalPages;
                                    handlePageChange(currentPage + 1, { force: needForce });
                                  }}
                                  disabled={isDisabled}
                                  className={`px-4 py-2 rounded-lg font-medium border ${
                                    isDisabled
                                      ? "bg-black/40 text-gray-500 border-white/10 cursor-not-allowed"
                                      : "bg-[#181818] text-white border-white/10 hover:bg-[#222]"
                                  }`}
                                >
                                  {liveLoading ? "Loading..." : fetchingMore ? "Loading..." : "Next"}
                                </button>
                              );
                            })()}
                          </div>

                          {/* Results Range */}
                          {totalItems > 0 && (currentPage - 1) * itemsPerPage < totalItems && (
                            <div className="text-gray-400 text-sm mt-2 results-range">
                              Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} results
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <div className="text-center text-gray-500 text-xl font-semibold py-12">
                    No data available
                  </div>
                );
              })()}
            </div>
          ) : (
            !loading && (
              <div className="w-full flex-1 flex flex-col items-center justify-center">
                <div className="text-center text-gray-500 text-2xl font-semibold py-12">
                  No data available
                </div>
                <div className="min-h-[50vh]"></div>
              </div>
            )
          )}
        </main>
      </div>

      {/* Scroll To Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-2 z-50 bg-white text-neutral-500 rounded-full shadow-lg w-12 h-12 flex items-center justify-center transition hover:bg-neutral-50 border-2 border-neutral-500 group"
          aria-label="Scroll to top"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-6 w-6 transition-transform group-hover:-translate-y-1" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
            strokeWidth={2}
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}

      {/* Footer remains unchanged as it already has the correct padding */}
      <div className="mt-12 relative z-10">
        <Footer />
      </div>
    </div>
  );
};

export default DiscoverPage;

// Create a stable uid for each item
const makeUID = (item: any, fallbackIndex?: number) => {
  if (item.usecase_id) return String(item.usecase_id);
  if (item.id) return String(item.id);
  const titlePart = item.title ? item.title.replace(/\s+/g, "_").slice(0, 60) : "untitled";
  const sourcePart = Array.isArray(item.source) && item.source[0]
    ? (() => { try { return new URL(item.source[0]).hostname } catch { return item.source[0].slice(0,40) } })()
    : `no-src-${fallbackIndex ?? Math.floor(Math.random()*100000)}`;
  return `${titlePart}__${sourcePart}`;
};

// Add this helper function after the makeUID function
const removeDuplicatesByTitle = (items: any[]) => {
  const seen = new Set<string>();
  return items.filter(item => {
    const title = item.title?.toLowerCase().trim();
    if (!title || seen.has(title)) {
      return false;
    }
    seen.add(title);
    return true;
  });
};

// Modify the normalizeItems function
const normalizeItems = (arr: any[] = []) => {
  const withUID = arr.map((it, i) => {
    const _uid = it._uid ?? makeUID(it, i);
    return { ...it, _uid };
  });
  
  // Remove duplicates by title
  return removeDuplicatesByTitle(withUID);
};
